import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import { alpha, darken } from '@mantine/core';
import { getAcquisitionStatusPin, getInvestmentPin, getOtherPin, getUnknownMgmtTypePin } from '../components/pins';
import { landUseColors, propertyColors } from './constants';
import { MGMT_TYPE_FIELD, mgmtTypeVisuals, validMgmtTypeCodes } from './mgmtType';
import { ACQUISITION_STATUS_UNKNOWN, acquisitionStatusPreset } from './symbologyPresets';

/** Default picture-marker symbol dimensions used as the base for all pin renderers. */
export const defaultPictureMarker = {
  type: 'picture-marker',
  width: '28px',
  height: '38px'
};

const makeSymbol = (pinUrl: string) => ({
  ...defaultPictureMarker,
  url: pinUrl,
  width: `${32 * 1.2}px`,
  height: `${38 * 1.2}px`
} as any);

/** SimpleRenderer for "Other" category property pins (Welfare, PRI/Welfare, etc.). Retained only for legacy/out-of-scope ManagedBy consumers — do not reuse for MgmtType. */
export const otherPinRenderer = new SimpleRenderer({
  symbol: makeSymbol(getOtherPin())
});

/** SimpleRenderer for potential acquisition (investment) property pins. */
export const potentialAcquisitionPinRenderer = new SimpleRenderer({
  symbol: makeSymbol(getInvestmentPin())
});

/** SimpleRenderer for the gray `Unknown` `MgmtType` pin — used when a boundary feature has no confirmed layer-14 `MgmtType`. */
export const unknownMgmtTypePinRenderer = new SimpleRenderer({
  symbol: makeSymbol(getUnknownMgmtTypePin())
});

/**
 * Operation layer definitions — one entry per layer-14 `MgmtType` category
 * (`DirectOp`, `Lease`, `Contract`), replacing the previous four `ManagedBy`
 * pin-layer buckets. Definition expressions use the exact GIS codes from the
 * `ManagementType` domain and retain the existing `STATUS='Active'`
 * server-side scope predicate. Colors/pins/labels are sourced from the
 * shared `mgmtTypeVisuals` contract so this list can never drift from the
 * boundary renderer, cluster symbols, or filtered-pin renderer below.
 *
 * Confirmed against
 * packages/map/docs/map-service-defs/jarvis/igp-gis-layers/properties-boundaries.json
 * (layer id 14, `MgmtType`/`ManagementType` domain, `STATUS`/`PropertyStatus` domain).
 */
export const operationLayers = validMgmtTypeCodes.map((code) => ({
  id: `mgmt-type-${code}`,
  title: mgmtTypeVisuals[code].label,
  definitionExpression: `${MGMT_TYPE_FIELD}='${code}' and STATUS='Active'`,
  value: code,
  renderer: new SimpleRenderer({ symbol: makeSymbol(mgmtTypeVisuals[code].getPin()) })
}));

/**
 * UniqueValueRenderer keyed on `MgmtType` for showing filtered property
 * table results — single source of truth for pin icons across grid and map
 * for the boundary-map presentation. Unrecognized/null/blank values
 * (including any legacy `ManagedBy`-only rows) resolve to the shared gray
 * `Unknown` pin rather than any valid category or land-use value.
 */
export const filteredPropertyPinRenderer = new UniqueValueRenderer({
  field: MGMT_TYPE_FIELD,
  uniqueValueInfos: validMgmtTypeCodes.map((code) => ({
    value: code,
    symbol: makeSymbol(mgmtTypeVisuals[code].getPin()),
  })),
  defaultSymbol: makeSymbol(mgmtTypeVisuals.Unknown.getPin()),
});

const simpleLineSymbol = (color:any)=> ({
  type: 'simple-fill',
  color: alpha(darken(color,.9),.1),
  outline: { color: color, width: 2 },
}) as const

/**
 * UniqueValueRenderer that colors property boundary fills and outlines by
 * layer-14 `MgmtType` category. Null/blank/unexpected values render with the
 * shared gray `Unknown` treatment (`mgmtTypeVisuals.Unknown`) — a distinct
 * fill/outline from all three valid categories — rather than the previous
 * transparent/"no category" default.
 */
export const propertyBoundaryRenderer = new UniqueValueRenderer({
  field: MGMT_TYPE_FIELD,
  defaultSymbol: simpleLineSymbol(mgmtTypeVisuals.Unknown.color),
  uniqueValueInfos: validMgmtTypeCodes.map((code) => ({
    value: code,
    symbol: simpleLineSymbol(mgmtTypeVisuals[code].color)
  }))
})

/** UniqueValueRenderer that colors leased property boundaries by `Type` (tillable, pasture, CRP, etc.). */
export const leasePropertyRenderer = {
  // @ts-ignore
  type: 'unique-value',
  field: 'Type',
  defaultSymbol: { type: 'simple-fill' },
  uniqueValueInfos: [{
    value: 'Non-Tillable',
    symbol: {
      type: 'simple-fill',
      color: landUseColors.nonTillable,
      outline: {
        width: '0.5px',
        color: 'rgba(215, 208, 124, 1)'
      }
    }
  },
    {
      value: 'Irrigated Tillable',
      symbol: {
        type: 'simple-fill',
        color: landUseColors.irrigatedTillable,
        outline: {
          width: '0.5px',
          color: 'rgba(158, 168, 219, 1)'
        }
      }
    },
    {
      value: 'Dryland Tillable',
      symbol: {
        type: 'simple-fill',
        color: landUseColors.dryLandTillable,
        outline: {
          width: '0.5px',
          color: 'rgba(108, 156, 121, 1)'
        }
      }
    },
    {
      value: 'Pasture',
      symbol: {
        type: 'simple-fill',
        color: landUseColors.pasture,
        outline: {
          width: '0.5px',
          color: 'rgba(183, 154, 195, 1)'
        }
      }
    },
    {
      value: 'Government Contract (CRP)',
      symbol: {
        type: 'simple-fill',
        color: landUseColors.governmentContract,
        outline: {
          width: '0.5px',
          color: 'rgba(245, 183, 116, 1)'
        }
      }
    }]

}

// ── Potential Acquisition (investment) boundary renderer ────────────────────
// Keyed on a two-field Arcade valueExpression that maps native GIS fields
// (PotentialStatus + STATUS) onto four visual states so the layer can be a
// live url-based FeatureLayer with no client-side aimStatus enrichment.
//
// acquisitionStatusWhereMap provides the server-side WHERE equivalent for each
// visual value — used by MapTools (legend filter) and MapProvider (default filter).

/**
 * Maps each acquisition visual-state value to its equivalent native GIS
 * WHERE clause (PotentialStatus only — STATUS is not used for filtering).
 */
export const acquisitionStatusWhereMap: Record<string, string> = {
  active:    `PotentialStatus NOT IN ('Completed','Discontinued')`,
  completed: `PotentialStatus = 'Completed'`,
  historic:  `PotentialStatus = 'Discontinued'`,
};

/** Default WHERE for "active only" — excludes completed and discontinued. */
export const acquisitionActiveOnlyWhere =
  `PotentialStatus NOT IN ('Completed','Discontinued')`;

const acquisitionStatusSymbols = {
  active: {
    type: 'simple-fill',
    // color: `${alpha(darken(propertyColors.investment, 0.25), 0.15)}`,
    color: `${alpha(darken(propertyColors.investment, 0.2), 0.15)}`, // green
    // outline: { color: propertyColors.investment, width: 2 }
    outline: { color: propertyColors.investment, width: 2 }
  },
  completed: {
    type: 'simple-fill',
    color: `${alpha(darken(propertyColors.investmentCompleted, 0.2), 0.15)}`, // green
    outline: { color: propertyColors.investmentCompleted, width: 2 }
  },
  historic: {
    type: 'simple-fill',
    // color: `${alpha(darken('#ff9f27', 0.25), 0.15)}`, // orange-ish
    // // style: 'diagonal-cross',
    // outline: { color: 'rgba(255, 201, 0,.50)', width: 2, style: 'solid', join: 'miter', miterLimit: 1 }
    color: `${alpha(propertyColors.investmentHistorical, 0.2)}`,
    outline: { color: propertyColors.investmentHistorical, width: 2 }
  }
} as const;
/**
 * Arcade expression that maps PotentialStatus to three visual state codes.
 * STATUS is intentionally not used — PotentialStatus is the single source of truth.
 */

const acquisitionArcadeExpression = `
  var ps = $feature.PotentialStatus;
  if (ps == 'Discontinued') { return 'historic'; }
  if (ps == 'Completed')    { return 'completed'; }
  return 'active';`.trim();

/**
* Creates a UniqueValueRenderer for potential acquisition boundaries, using an Arcade expression
* to map `PotentialStatus` to three visual states: Active, Completed, and Discontinued/Historic.
*
* @returns A configured UniqueValueRenderer with per-status fill and outline symbols.
*/
export const createAcquisitionRenderer = () =>
  new UniqueValueRenderer({
    valueExpression: acquisitionArcadeExpression,
    valueExpressionTitle: 'Acquisition Status',
    defaultSymbol: { ...acquisitionStatusSymbols.active },
    uniqueValueInfos: [
      { value: 'active',    label: 'Active',                  symbol: { ...acquisitionStatusSymbols.active } },
      { value: 'completed', label: 'Completed',               symbol: { ...acquisitionStatusSymbols.completed } },
      { value: 'historic',  label: 'Discontinued / Historic', symbol: { ...acquisitionStatusSymbols.historic } },
    ]
  });

// ── Acquisition phase/status preset boundary renderer (PRM-151) ─────────────
//
// Colors acquisition boundaries directly by the GIS-coded `PotentialStatus`
// field (confirmed against
// packages/map/docs/map-service-defs/aim/opportunityeditmap/potential-acquisitions.json),
// rather than the collapsed 3-state Arcade expression above. Null, blank, or
// unexpected values fall through to `defaultSymbol` — the shared `Unknown`
// category color from `acquisitionStatusPreset` — so they render as an
// explicit unknown state rather than silently joining a valid category
// (spec sections 7.3.5 and 9).
/**
 * Creates a `UniqueValueRenderer` for acquisition boundaries keyed on the raw
 * `PotentialStatus` field, using the shared `acquisitionStatusPreset` category
 * colors so the Style tab's phase/status preset renders consistently with its
 * toggle UI.
 *
 * @returns A configured `UniqueValueRenderer` with one symbol per confirmed
 * `PotentialStatus` code and an explicit `Unknown` `defaultSymbol`.
 */
export const createAcquisitionStatusBoundaryRenderer = () => {
  const unknownCategory = acquisitionStatusPreset.categories.find(
    (category) => category.value === ACQUISITION_STATUS_UNKNOWN
  )!;
  const knownCategories = acquisitionStatusPreset.categories.filter(
    (category) => category.value !== ACQUISITION_STATUS_UNKNOWN
  );

  return new UniqueValueRenderer({
    field: acquisitionStatusPreset.field,
    defaultSymbol: simpleLineSymbol(unknownCategory.color),
    uniqueValueInfos: knownCategories.map((category) => ({
      value: category.value,
      label: category.label,
      symbol: simpleLineSymbol(category.color)
    }))
  });
};

// ── Acquisition phase/status preset pin renderer (PRM-151) ─────────────────
//
// Colors unclustered acquisition pins by the same raw `PotentialStatus`
// field/categories/colors as `createAcquisitionStatusBoundaryRenderer`, using
// the shared dynamic SVG pin builder (`customizePin`/`getAcquisitionStatusPin`)
// so a single category-color source of truth drives both boundary fills and
// pin colors (spec §7.2.3). Null, blank, or unexpected values fall through to
// `defaultSymbol` — the shared `Unknown` category color — same as the
// boundary renderer.
//
// IMPORTANT: this renderer must only ever be assigned to the *unclustered*
// point renderer (`FeatureLayer.renderer`), never to a cluster's
// `featureReduction.symbol`. A cluster can aggregate features spanning
// multiple `PotentialStatus` values, so coloring a cluster by one phase color
// would misrepresent its (possibly mixed) membership. Clusters must remain
// the existing neutral/static `createClusterLayerSymbol('potentialAcquisition')`
// symbol regardless of which Style preset is active.
/**
 * Creates a `UniqueValueRenderer` for unclustered acquisition pins keyed on
 * the raw `PotentialStatus` field, using the same `acquisitionStatusPreset`
 * category colors as `createAcquisitionStatusBoundaryRenderer` so the pin and
 * boundary layers render consistently with the Style tab's phase/status
 * preset and its toggle UI.
 *
 * Do not use this renderer's colors for a cluster symbol — see module note
 * above; mixed-status clusters cannot honestly be painted one phase color.
 *
 * @returns A configured `UniqueValueRenderer` with one pin symbol per
 * confirmed `PotentialStatus` code and an explicit `Unknown` `defaultSymbol`.
 */
export const createAcquisitionStatusPinRenderer = () => {
  const unknownCategory = acquisitionStatusPreset.categories.find(
    (category) => category.value === ACQUISITION_STATUS_UNKNOWN
  )!;
  const knownCategories = acquisitionStatusPreset.categories.filter(
    (category) => category.value !== ACQUISITION_STATUS_UNKNOWN
  );

  return new UniqueValueRenderer({
    field: acquisitionStatusPreset.field,
    defaultSymbol: makeSymbol(getAcquisitionStatusPin(unknownCategory.color)),
    uniqueValueInfos: knownCategories.map((category) => ({
      value: category.value,
      label: category.label,
      symbol: makeSymbol(getAcquisitionStatusPin(category.color))
    }))
  });
};
